import { useEffect, useMemo, useState } from "react";
import { gymClient, ApiError } from "../../apiClient/gymClient";
import type { GymProfile } from "../../types/gym.types";

function EmployeeRow({ employee, onUpdated }: { employee: GymProfile; onUpdated: (p: GymProfile) => void }) {
  const [noShowDraft, setNoShowDraft] = useState(String(employee.noShowCount));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noShowDirty = noShowDraft !== String(employee.noShowCount);

  async function saveNoShowCount() {
    const value = Number(noShowDraft);
    if (!Number.isInteger(value) || value < 0) {
      setError("Must be a non-negative whole number.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onUpdated(await gymClient.updateAdminEmployee(employee.userId, { noShowCount: value }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleBlocked() {
    setBusy(true);
    setError(null);
    try {
      onUpdated(await gymClient.updateAdminEmployee(employee.userId, { isBlocked: !employee.isBlocked }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="booking-row">
      <span className="attendee">
        <span className="attendee-name">{employee.fullName}</span>
        <span className="attendee-mobile">{employee.mobileNumber}</span>
      </span>

      <span className="admin-noshow">
        <label htmlFor={`noshow-${employee.userId}`}>No-shows</label>
        <input
          id={`noshow-${employee.userId}`}
          type="number"
          min={0}
          value={noShowDraft}
          onChange={(e) => setNoShowDraft(e.target.value)}
          disabled={busy}
        />
        {noShowDirty && (
          <button type="button" className="btn btn-ghost" onClick={saveNoShowCount} disabled={busy}>
            Save
          </button>
        )}
      </span>

      {employee.isBlocked ? (
        <span className="status-badge status-badge--no-show">Blocked</span>
      ) : (
        <span className="status-badge status-badge--attended">Active</span>
      )}

      <button
        type="button"
        className={employee.isBlocked ? "btn btn-primary" : "btn btn-danger-ghost"}
        onClick={toggleBlocked}
        disabled={busy}
      >
        {employee.isBlocked ? "Unblock" : "Block"}
      </button>

      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

export function AdminEmployeesTab() {
  const [employees, setEmployees] = useState<GymProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    void gymClient
      .getAdminEmployees()
      .then(setEmployees)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load employees."))
      .finally(() => setIsLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => e.fullName.toLowerCase().includes(q) || e.mobileNumber.includes(q));
  }, [employees, query]);

  function handleUpdated(updated: GymProfile) {
    setEmployees((prev) => prev.map((e) => (e.userId === updated.userId ? updated : e)));
  }

  if (error) return <div className="state-panel"><h2>Couldn&rsquo;t load employees</h2><p>{error}</p></div>;
  if (isLoading) return <div className="state-panel">Loading…</div>;

  return (
    <div>
      <input
        type="text"
        className="coach-search"
        placeholder="Search by name or mobile…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search employees"
      />
      {filtered.length === 0 ? (
        <div className="state-panel">
          <p>{employees.length === 0 ? "No completed profiles yet." : "No one matches that search."}</p>
        </div>
      ) : (
        <div className="bookings-day admin-list-wrap">
          <ul className="bookings-day-items">
            {filtered.map((e) => (
              <EmployeeRow key={e.userId} employee={e} onUpdated={handleUpdated} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
